// index.js — Full React App with Grievance + Love Letter Portal
import React, { useState, useEffect, useRef } from 'react';
import { createRoot } from 'react-dom/client';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import Container from '@mui/material/Container';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Slider from '@mui/material/Slider';
import Paper from '@mui/material/Paper';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Divider from '@mui/material/Divider';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Snackbar from '@mui/material/Snackbar';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import { motion } from 'framer-motion';

import { initializeApp } from 'firebase/app';
import { getFirestore, collection, addDoc, getDocs, doc, updateDoc } from 'firebase/firestore';

const firebaseConfig = {
  apiKey: "AIzaSyCg6YTlyJH_wDk7lMml3b6AQdLtHfad_Os",
  authDomain: "grievance-portal-c38d2.firebaseapp.com",
  projectId: "grievance-portal-c38d2",
  storageBucket: "grievance-portal-c38d2.firebasestorage.app",
  messagingSenderId: "92846872073",
  appId: "1:92846872073:web:7cf5c4b60b18ff6753cad8"
};
const app = initializeApp(firebaseConfig);
const db = getFirestore(app);

const theme = createTheme({
  palette: {
    mode: 'dark',
    background: {
      default: '#121212',
      paper: '#1e1e1e'
    },
    primary: {
      main: '#bb86fc'
    },
    secondary: {
      main: '#03dac6'
    },
    text: {
      primary: '#ffffff',
      secondary: '#b0b0b0'
    }
  },
  shape: {
    borderRadius: 4
  },
  typography: {
    fontFamily: 'DM Sans, sans-serif'
  },
  components: {
    MuiToggleButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 500
        }
      }
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600
        }
      }
    }
  }
});

const moodMap = {
  love: ['🥰', '😘', '😍', '🫦', '🤰'],
  grievance: ['🥺', '😡', '😔', '☹️', '💀']
};

const intensityMarks = {
  love: ['Slight Flutter 💓','Heart Tingling 💘','Romantic Overload 💕','Dangerous Crush 💍','Fully In Love 🫠'],
  grievance: ['Minor Eye Roll 🙄','Passive-Aggressive Stare 😐','Mild Annoyance 😤','Internal Screaming 😠','Existential Rage 💢']
};

const moodMessages = {
  '🥰': 'Aww, you softie 🧸',
  '😘': 'A kiss a day keeps the breakup away 💋',
  '😍': 'Someone’s eyes are heart-shaped 😍',
  '🫦': 'Okay... chill. 😳',
  '🤰': '...we need to talk 😶',
  '🥺': 'Classic guilt trip move 🥺',
  '😡': 'Anger level: dragon 🐲',
  '😔': 'Cue sad violin 🎻',
  '☹️': 'Pouting into the void ☁️',
  '💀': 'Dramatic. But valid. 💀'
};

function BackgroundVideo({ src }) {
  const videoRef = useRef();

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.load();
      videoRef.current.play().catch(() => {});
    }
  }, [src]); // <-- run this effect every time `src` changes

  return (
    <video
      ref={videoRef}
      autoPlay
      loop
      muted
      playsInline
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        objectFit: 'cover',
        zIndex: -1,
        pointerEvents: 'none',
        backgroundColor: 'black'
      }}
    >
      <source src={src} type="video/mp4" />
    </video>
  );
}

function App() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loggedIn, setLoggedIn] = useState(false);
  const [mode, setMode] = useState('grievance');
  const [title, setTitle] = useState('');
  const [desc, setDesc] = useState('');
  const [mood, setMood] = useState('');
  const [severity, setSeverity] = useState(3);
  const [grievances, setGrievances] = useState([]);
  const [toastOpen, setToastOpen] = useState(false);
  const [filter, setFilter] = useState('all');
  const [submitting, setSubmitting] = useState(false);
  const [backgroundSrc, setBackgroundSrc] = useState('/vega_grieve_02.mp4');

  useEffect(() => {
    setBackgroundSrc(loggedIn ? '/vega_grieve_01.mp4' : '/vega_grieve_02.mp4');
  }, [loggedIn]);

  const handleLogin = () => {
    if (username === 'vegabottega' && password === 'ikindalovearz') {
      setLoggedIn(true);
      fetchGrievances();
    } else {
      alert('Incorrect credentials 🤡');
    }
  };

  const handleSubmit = async () => {
    if (!title || !desc) return;
    setToastOpen(true);
    setSubmitting(true);
    const newEntry = {
      title,
      description: desc,
      mood,
      severity,
      type: mode,
      resolved: false,
      timestamp: new Date().toISOString()
    };
    const docRef = await addDoc(collection(db, 'grievances'), newEntry);
    const newGrievance = { id: docRef.id, ...newEntry };
    setGrievances(prev => [newGrievance, ...prev]);
    setTitle('');
    setDesc('');
    setMood('');
    setSeverity(3);
    setSubmitting(false);
  };

  const fetchGrievances = async () => {
    const snapshot = await getDocs(collection(db, 'grievances'));
    const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    const sorted = data.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
    setGrievances(sorted);
  };

  const resolveGrievance = async (id) => {
    const ref = doc(db, 'grievances', id);
    await updateDoc(ref, { resolved: true });
    setGrievances(prev => prev.map(g => g.id === id ? { ...g, resolved: true } : g));
  };

  const filteredGrievances = grievances.filter(g => filter === 'all' || g.type === filter);
  const moodOptions = moodMap[mode];
  const intensityLabels = intensityMarks[mode];

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <BackgroundVideo src={backgroundSrc} />

      {!loggedIn ? (
        <Container maxWidth="xs" sx={{ mt: 10 }}>
          <Paper elevation={3} sx={{ p: 4, borderRadius: '24px', border: '1px inset rgba(0,0,0,0.12)', backdropFilter: 'blur(8px)' }}>
            <Typography variant="h5" gutterBottom>🔐 Login to our portal</Typography>
            <TextField label="Username" fullWidth margin="normal" value={username} onChange={e => setUsername(e.target.value)} />
            <TextField label="Password" type="password" fullWidth margin="normal" value={password} onChange={e => setPassword(e.target.value)} />
            <Button variant="contained" color="primary" fullWidth sx={{ mt: 2 }} onClick={handleLogin}>
              Login
            </Button>
          </Paper>
        </Container>
      ) : (
        <Container maxWidth="sm" sx={{ mt: 4 }}>
          <Paper elevation={3} sx={{ p: 4, borderRadius: '24px', border: '1px inset rgba(0,0,0,0.12)', backdropFilter: 'blur(8px)', maxHeight: '85vh', overflowY: 'auto' }}>
  <ToggleButtonGroup
    color="primary"
    value={mode}
    exclusive
    fullWidth
    onChange={(e, newMode) => setMode(newMode)}
    sx={{ mb: 2 }}
  >
    <ToggleButton value="love">💌 Love Letter</ToggleButton>
    <ToggleButton value="grievance">🙄 Grievance</ToggleButton>
  </ToggleButtonGroup>

  <Typography variant="h5" gutterBottom sx={{ mt: 1 }}>
    {mode === 'love' ? 'Write a Love Letter' : 'Submit a Grievance'}
  </Typography>

  <TextField label="Title" fullWidth margin="normal" value={title} onChange={e => setTitle(e.target.value)} />
  <TextField
    label="Description"
    fullWidth
    margin="normal"
    multiline
    rows={4}
    value={desc}
    onChange={e => setDesc(e.target.value)}
  />

  <Box mt={2}>
    <Typography gutterBottom>Mood</Typography>
    <Box display="flex" gap={2} justifyContent="space-between" flexWrap="wrap">
      {moodOptions.map(m => (
        <motion.div whileTap={{ scale: 1.2 }} key={m}>
          <Button
            variant={m === mood ? 'contained' : 'outlined'}
            onClick={() => setMood(m)}
            sx={{ fontSize: '2rem', minWidth: 0, width: 56, height: 56 }}
          >
            {m}
          </Button>
        </motion.div>
      ))}
    </Box>
    {mood && (
      <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic', color: 'text.secondary' }}>
        {moodMessages[mood]}
      </Typography>
    )}
  </Box>

  <Box mt={2}>
    <Typography gutterBottom>
      {mode === 'love' ? 'Intensity' : 'Severity'}: {intensityLabels[severity - 1]}
    </Typography>
    <Slider value={severity} min={1} max={5} step={1} onChange={(e, val) => setSeverity(val)} />
  </Box>

  <Button
    variant="contained"
    color="primary"
    fullWidth
    sx={{ mt: 3 }}
    onClick={handleSubmit}
    disabled={submitting}
  >
    {mode === 'love' ? 'Send Love Letter' : 'Submit Grievance'}
  </Button>
</Paper>

<Box mt={5}>
  <Typography variant="h6" gutterBottom>🕰️ History</Typography>

  <ToggleButtonGroup
    value={filter}
    exclusive
    onChange={(e, val) => setFilter(val)}
    fullWidth
    sx={{ mb: 2 }}
  >
    <ToggleButton value="all">All</ToggleButton>
    <ToggleButton value="love">Love</ToggleButton>
    <ToggleButton value="grievance">Grievance</ToggleButton>
  </ToggleButtonGroup>

  {filteredGrievances.length === 0 ? (
    <Typography sx={{ textAlign: 'center', mt: 4, fontStyle: 'italic' }}>
      {filter === 'grievance'
        ? 'No complaints yet? How suspicious...'
        : filter === 'love'
        ? 'No love notes? Wow. Cold.'
        : 'There is... absolutely nothing here.'}
    </Typography>
  ) : (
    <Paper sx={{ borderRadius: '24px', overflow: 'hidden' }}>
      <List>
        {filteredGrievances.map((g, i) => (
          <React.Fragment key={i}>
            <ListItem
              alignItems="flex-start"
              secondaryAction={
                g.type === 'grievance' && !g.resolved ? (
                  <Button size="small" color="secondary" onClick={() => resolveGrievance(g.id)} sx={{ fontWeight: 700, textTransform: 'none' }}>
                    mark as resolved
                  </Button>
                ) : g.resolved ? (
                  <Typography variant="caption" color="success.main">✅ resolved</Typography>
                ) : null
              }
            >
              <ListItemText
                primary={`${g.title} ${g.mood} (${g.type === 'love' ? 'Intensity' : 'Severity'} ${g.severity})`}
                secondary={`${g.description} — ${new Date(g.timestamp).toLocaleString()}`}
              />
            </ListItem>
            <Divider component="li" />
          </React.Fragment>
        ))}
      </List>
    </Paper>
  )}
</Box>

<Snackbar
  anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
  open={toastOpen}
  autoHideDuration={3000}
  onClose={() => setToastOpen(false)}
  message="Your feelings have been ignored 💅"
  action={
    <IconButton size="small" color="inherit" onClick={() => setToastOpen(false)}>
      <CloseIcon fontSize="small" />
    </IconButton>
  }
/>
        </Container>
      )}
    </ThemeProvider>
  );
}

const container = document.getElementById('root');
const root = createRoot(container);
root.render(<App />);